import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

export interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  /** Sticky footer rendered below the scrollable content area */
  footer?: React.ReactNode;
  /** Extra classes applied to the sheet panel */
  className?: string;
  /** Initial snap point height. Default: '65vh' */
  defaultSnap?: string;
}

type SnapPoint = 'partial' | 'full' | 'closed';

const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  className = '',
  defaultSnap = '65vh',
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);

  // 'partial' | 'full' | 'closed'
  const [snap, setSnap] = useState<SnapPoint>('closed');
  // live drag offset (px from current snap, positive = dragged down)
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const dragStartY = useRef(0);
  const dragStartOffset = useRef(0);

  // Open/close
  useEffect(() => {
    if (isOpen) {
      setSnap('partial');
      setDragOffset(0);
    } else {
      setSnap('closed');
      setDragOffset(0);
    }
  }, [isOpen]);

  // Lock body scroll while open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const getSnapHeight = (s: SnapPoint): string => {
    if (s === 'full') return '100dvh';
    if (s === 'partial') return defaultSnap;
    return '0px';
  };

  // ── Touch handlers ───────────────────────────────────────────────────────
  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      dragStartY.current = e.touches[0].clientY;
      dragStartOffset.current = dragOffset;
      setIsDragging(true);
      setIsAnimating(false);
    },
    [dragOffset]
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging) return;
      const delta = e.touches[0].clientY - dragStartY.current;
      setDragOffset(dragStartOffset.current + delta);
    },
    [isDragging]
  );

  const onTouchEnd = useCallback(() => {
    setIsDragging(false);
    setIsAnimating(true);

    const threshold = 60; // px to commit a snap change

    if (snap === 'partial') {
      if (dragOffset < -threshold) {
        // dragged up → snap to full
        setSnap('full');
      } else if (dragOffset > threshold) {
        // dragged down → close
        onClose();
      }
    } else if (snap === 'full') {
      if (dragOffset > threshold) {
        // dragged down → snap to partial
        setSnap('partial');
      }
    }

    setDragOffset(0);
  }, [snap, dragOffset, onClose]);

  // ── Backdrop click ───────────────────────────────────────────────────────
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (typeof window === 'undefined') return null;

  // Build inline style for height and drag transform
  const sheetStyle: React.CSSProperties = {
    height: getSnapHeight(snap === 'closed' ? 'partial' : snap),
    transform: isDragging
      ? `translateY(${Math.max(dragOffset, snap === 'full' ? -0 : 0)}px)`
      : snap === 'closed'
        ? 'translateY(100%)'
        : 'translateY(0)',
    transition: isDragging
      ? 'none'
      : isAnimating
        ? 'transform 0.3s cubic-bezier(0.32,0.72,0,1), height 0.3s cubic-bezier(0.32,0.72,0,1)'
        : 'transform 0.35s cubic-bezier(0.32,0.72,0,1), height 0.35s cubic-bezier(0.32,0.72,0,1)',
    borderRadius: snap === 'full' && !isDragging ? '0' : '16px 16px 0 0',
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-[9999] flex flex-col justify-end ${
        isOpen ? 'pointer-events-auto' : 'pointer-events-none'
      }`}
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${
          isOpen ? 'opacity-50' : 'opacity-0'
        }`}
      />

      {/* Sheet panel */}
      <div
        ref={sheetRef}
        style={sheetStyle}
        className={`relative w-full bg-white shadow-2xl flex flex-col overflow-hidden ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle area — touch target */}
        <div
          ref={handleRef}
          className="flex justify-center pt-3 pb-2 shrink-0 cursor-grab active:cursor-grabbing touch-none"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        {/* Header */}
        {title && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            <button
              onClick={onClose}
              className="p-1 rounded-full hover:bg-gray-100 text-gray-500"
              aria-label="Close"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="overflow-y-auto flex-1">{children}</div>

        {/* Sticky footer */}
        {footer && <div className="shrink-0">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default BottomSheet;
